import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Skeleton,
  useTheme
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LineChart,
  Line,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  TooltipProps
} from 'recharts';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaidIcon from '@mui/icons-material/Paid';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface DashboardData {
  totalSales: number;
  totalExpenses: number;
  netIncome: number;
  salesByMonth: {
    month: string;
    amount: number;
  }[];
  expensesByCategory: {
    category: string;
    amount: number;
  }[];
  topSellingItems: {
    name: string;
    quantity: number;
    revenue: number;
  }[];
}

const COLORS = [
  '#3f51b5', '#009688', '#ff9800', '#f44336', '#673ab7',
  '#2196f3', '#4caf50', '#ffeb3b', '#9c27b0', '#00bcd4'
];

// Fix the custom tooltip type
const CustomTooltip = ({ active, payload, label }: TooltipProps<number, string>) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: 'background.paper',
          p: 2,
          boxShadow: '0 2px 10px rgba(0,0,0,0.15)',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2">{label}</Typography>
        {payload.map((entry, index) => (
          <Typography 
            key={`item-${index}`} 
            variant="body2"
            sx={{ color: entry.color, fontWeight: 500 }}
          >
            {entry.name}: ₹{entry.value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </Typography>
        ))}
      </Box>
    );
  }
  return null;
};

// Summary Card component
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SummaryCard = ({ 
  title, 
  value, 
  icon, 
  trend = 0, 
  loading = false 
}: { 
  title: string; 
  value: number; 
  icon: React.ReactNode; 
  trend?: number;
  loading?: boolean;
}) => {
  const isPositive = trend >= 0;
  
  return (
    <Card 
      className="card-hover"
      sx={{ 
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'visible',
      }}
    >
      <CardContent sx={{ flex: '1 0 auto', p: 3 }}>
        <Box
          sx={{
            position: 'absolute',
            top: -15,
            left: 20,
            bgcolor: 'primary.main',
            color: '#fff',
            width: 50,
            height: 50,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          {icon}
        </Box>
        
        <Box sx={{ textAlign: 'right' }}>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 0.5, fontWeight: 500 }}
          >
            {title}
          </Typography>
          
          {loading ? (
            <Skeleton variant="text" width="100%" height={40} />
          ) : (
            <Typography 
              variant="h4" 
              sx={{ fontWeight: 700, letterSpacing: '-0.5px' }}
            >
              ₹{value.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              })}
            </Typography>
          )}
          
          {trend !== 0 && !loading && (
            <Typography 
              variant="body2" 
              color={isPositive ? 'success.main' : 'error.main'}
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                mt: 0.5
              }}
            >
              {isPositive ? <TrendingUpIcon fontSize="small" sx={{ mr: 0.5 }} /> : <TrendingDownIcon fontSize="small" sx={{ mr: 0.5 }} />}
              {isPositive ? '+' : ''}{trend.toFixed(1)}% from last month
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export function Dashboard() {
  const [data, setData] = useState<DashboardData>({
    totalSales: 0,
    totalExpenses: 0,
    netIncome: 0,
    salesByMonth: [],
    expensesByCategory: [],
    topSellingItems: [],
  });
  const [loading, setLoading] = useState(true);
  const theme = useTheme();

  const chartConfig = {
    background: theme.palette.background.paper,
    textColor: theme.palette.text.primary,
    tooltipBackground: theme.palette.background.paper,
    gridColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const [salesRes, expensesRes, saleItemsRes] = await Promise.all([
        supabase.from('sales').select('total_amount, created_at'),
        supabase.from('expenses').select('amount, category, created_at'),
        supabase.from('sale_items').select('quantity, price_at_sale, items(name)')
      ]);

      if (salesRes.error) throw salesRes.error;
      if (expensesRes.error) throw expensesRes.error;
      if (saleItemsRes.error) throw saleItemsRes.error;

      const salesData = salesRes.data || [];
      const expensesData = expensesRes.data || [];
      const saleItemsData = saleItemsRes.data || [];

      if (salesData && expensesData && saleItemsData) {
        // Calculate totals
        const totalSales = salesData.reduce((sum, sale) => sum + sale.total_amount, 0);
        const totalExpenses = expensesData.reduce((sum, expense) => sum + expense.amount, 0);

        // Process sales by month
        const salesByMonth = processSalesByMonth(salesData);

        // Process expenses by category
        const expensesByCategory = processExpensesByCategory(expensesData);

        // Process top selling items
        const topSellingItems = processTopSellingItems(saleItemsData);

        setData({
          totalSales,
          totalExpenses,
          netIncome: totalSales - totalExpenses,
          salesByMonth,
          expensesByCategory,
          topSellingItems,
        });
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      // Delay loading state change to show smooth transitions
      setTimeout(() => setLoading(false), 600);
    }
  };

  const processSalesByMonth = (salesData: any[]) => {
    const monthlyData: { [key: string]: number } = {};
    salesData.forEach((sale) => {
      const date = new Date(sale.created_at);
      const monthYear = `${date.toLocaleString('default', { month: 'short' })} ${date.getFullYear()}`;
      monthlyData[monthYear] = (monthlyData[monthYear] || 0) + sale.total_amount;
    });

    return Object.entries(monthlyData).map(([month, amount]) => ({
      month,
      amount,
    })).sort((a, b) => {
      // Sort by date (assuming format is "MMM YYYY")
      const [aMonth, aYear] = a.month.split(' ');
      const [bMonth, bYear] = b.month.split(' ');
      // Use a more reliable comparison for month names
      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      const yearDiff = parseInt(aYear) - parseInt(bYear);
      if (yearDiff !== 0) return yearDiff;
      return months.indexOf(aMonth) - months.indexOf(bMonth);
    });
  };

  const processExpensesByCategory = (expensesData: any[]) => {
    const categoryData: { [key: string]: number } = {};
    expensesData.forEach((expense) => {
      categoryData[expense.category] = (categoryData[expense.category] || 0) + expense.amount;
    });

    return Object.entries(categoryData)
      .map(([category, amount]) => ({
        category,
        amount,
      }))
      .sort((a, b) => b.amount - a.amount); // Sort by amount descending
  };

  const processTopSellingItems = (saleItemsData: any[]) => {
    const itemSales: { [key: string]: { quantity: number; revenue: number; name: string } } = {};
    saleItemsData.forEach((item) => {
      const itemName = item.items?.name || 'Unknown';
      if (!itemSales[itemName]) {
        itemSales[itemName] = { quantity: 0, revenue: 0, name: itemName };
      }
      itemSales[itemName].quantity += item.quantity || 0;
      itemSales[itemName].revenue += (item.quantity || 0) * (item.price_at_sale || 0);
    });

    return Object.values(itemSales)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  };

  if (loading) {
    return <LoadingSpinner message="Preparing your dashboard..." />;
  }

  return (
    <Box className="fade-in" sx={{ pt: 2 }}>
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Sales"
            value={data.totalSales}
            icon={<ShoppingBasketIcon />}
            trend={5.2} // Example trend percentage
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Total Expenses"
            value={data.totalExpenses}
            icon={<AccountBalanceWalletIcon />}
            trend={-2.1} // Example trend percentage
            loading={loading}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <SummaryCard
            title="Net Income"
            value={data.netIncome}
            icon={<PaidIcon />}
            trend={3.8} // Example trend percentage
            loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Monthly Sales Chart */}
        <Grid item xs={12} lg={8}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 0,
              height: '100%',
              overflow: 'hidden',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
          >
            <CardHeader 
              title="Monthly Sales"
              titleTypographyProps={{ 
                variant: 'h6', 
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
              sx={{ px: 3, pt: 3, pb: 0 }}
            />
            <CardContent sx={{ height: 380, p: 0, overflowX: 'auto' }}>
              <Box sx={{ width: '100%', height: '100%', minWidth: 500 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart 
                    data={data.salesByMonth}
                    margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                  >
                    <defs>
                      <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={theme.palette.primary.main} stopOpacity={0.8} />
                        <stop offset="95%" stopColor={theme.palette.primary.main} stopOpacity={0.1} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke={chartConfig.gridColor} />
                    <XAxis 
                      dataKey="month" 
                      tick={{ fill: chartConfig.textColor }}
                      axisLine={{ stroke: chartConfig.gridColor }}
                    />
                    <YAxis 
                      tick={{ fill: chartConfig.textColor }}
                      axisLine={{ stroke: chartConfig.gridColor }}
                      tickFormatter={(value) => `₹${value}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="amount" 
                      stroke={theme.palette.primary.main} 
                      strokeWidth={3}
                      dot={{ 
                        r: 6, 
                        stroke: theme.palette.primary.main, 
                        strokeWidth: 1, 
                        fill: theme.palette.background.paper 
                      }}
                      activeDot={{ r: 8 }}
                      name="Sales" 
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Paper>
        </Grid>

        {/* Expenses by Category */}
        <Grid item xs={12} lg={4}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 0,
              height: '100%',
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)'
            }}
          >
            <CardHeader 
              title="Expenses by Category"
              titleTypographyProps={{ 
                variant: 'h6', 
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
              sx={{ px: 3, pt: 3, pb: 0 }}
            />
            <CardContent sx={{ height: 380, p: 0, overflowX: 'auto' }}>
              <Box sx={{ width: '100%', height: '100%', minWidth: 300, display: 'flex', justifyContent: 'center' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={data.expensesByCategory}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      innerRadius="45%"
                      outerRadius="70%"
                      paddingAngle={2}
                      label={({ cx, cy, midAngle, innerRadius, outerRadius, value, index }) => {
                        // Only show label for larger segments
                        if (!data.totalExpenses) return null;
                        const percentage = (value / data.totalExpenses) * 100;
                        if (percentage < 5) return null;
                        
                        const RADIAN = Math.PI / 180;
                        const radius = 25 + (innerRadius + (outerRadius - innerRadius));
                        const x = cx + radius * Math.cos(-midAngle * RADIAN);
                        const y = cy + radius * Math.sin(-midAngle * RADIAN);
                        
                        return (
                          <text 
                            x={x} 
                            y={y} 
                            fill={theme.palette.text.primary}
                            textAnchor={x > cx ? 'start' : 'end'} 
                            dominantBaseline="central"
                            style={{ fontSize: '12px', fontWeight: 500 }}
                          >
                            {data.expensesByCategory[index]?.category}
                          </text>
                        );
                      }}
                    >
                      {data.expensesByCategory.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Paper>
        </Grid>
        
        {/* Top Selling Items */}
        <Grid item xs={12}>
          <Paper 
            elevation={0}
            sx={{ 
              p: 0,
              borderRadius: 3,
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              mb: 3
            }}
          >
            <CardHeader 
              title="Top Selling Products"
              titleTypographyProps={{ 
                variant: 'h6', 
                fontWeight: 600,
                fontSize: '1.1rem'
              }}
              sx={{ px: 3, pt: 3, pb: 0 }}
            />
            <CardContent sx={{ height: 400, p: 0, overflowX: 'auto' }}>
              <Box sx={{ width: '100%', height: '100%', minWidth: 600 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={data.topSellingItems}
                    margin={{ top: 20, right: 30, left: 20, bottom: 70 }}
                    barSize={50}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={chartConfig.gridColor} />
                    <XAxis 
                      dataKey="name" 
                      tick={{ fill: chartConfig.textColor }}
                      axisLine={{ stroke: chartConfig.gridColor }}
                      tickLine={false}
                      textAnchor="end"
                      height={80}
                      angle={-35}
                      interval={0}
                    />
                    <YAxis 
                      yAxisId="left" 
                      orientation="left" 
                      stroke={theme.palette.primary.main}
                      tickFormatter={(value) => value.toString()}
                      axisLine={{ stroke: chartConfig.gridColor }}
                      tick={{ fill: chartConfig.textColor }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      stroke={theme.palette.secondary.main}
                      tickFormatter={(value) => `₹${value}`}
                      axisLine={{ stroke: chartConfig.gridColor }}
                      tick={{ fill: chartConfig.textColor }}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar 
                      yAxisId="left" 
                      dataKey="quantity" 
                      fill={theme.palette.primary.main}
                      radius={[4, 4, 0, 0]}
                      name="Units Sold" 
                    />
                    <Bar 
                      yAxisId="right" 
                      dataKey="revenue" 
                      fill={theme.palette.secondary.main} 
                      radius={[4, 4, 0, 0]}
                      name="Revenue (₹)" 
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}